import React from "react";

import { countryName } from "../../../../i18n/countries";
import { formatDate } from "../../../../utils/dates";

export function StepReview({ form, t, i18n }) {
  const lng = i18n.language?.startsWith("en") ? "en" : "de";
  // A12: wizard always captures addresses inline (customer_address_inline
  // / delivery_address_inline). The old id-to-object fallback via
  // user.saved_addresses is retired — the field is dormant on every user.
  const customer = form.customer_address_inline;
  // A17: always render BOTH addresses. If the user opted for
  // "delivery same as billing", the delivery block echoes the billing
  // one and shows a subtle "Same as Billing Address" badge above it.
  const deliverySame = Boolean(form.delivery_same_as_customer);
  const delivery = deliverySame ? customer : form.delivery_address_inline;

  const renderAddr = (a) =>
    a && (
      <div className="text-sm">
        <div className="font-semibold">{a.company}</div>
        <div className="text-xs text-text-secondary">
          {a.street}
          <br />
          {a.postal_code} {a.city}
          <br />
          {countryName(a.country, lng)}
        </div>
      </div>
    );

  return (
    <div className="space-y-6" data-testid="step-review">
      <div className="rounded-lg border border-border-subtle bg-surface p-4">
        <h3 className="mb-2 text-sm font-semibold">{t("wizard.event_heading")}</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-text-secondary">{t("wizard.event_name")}: </span>
            {form.event_name}
          </div>
          <div>
            <span className="text-text-secondary">{t("wizard.bottles_count")}: </span>
            {form.bottles_count}
          </div>
          <div>
            <span className="text-text-secondary">{t("wizard.from_date")}: </span>
            {form.from_date ? formatDate(form.from_date, i18n.language) : ""}
          </div>
          <div>
            <span className="text-text-secondary">{t("wizard.until_date")}: </span>
            {form.until_date ? formatDate(form.until_date, i18n.language) : ""}
          </div>
        </div>
        {form.remarks && (
          <div className="mt-2 text-xs italic text-text-secondary">{form.remarks}</div>
        )}
      </div>
      <div className="rounded-lg border border-border-subtle bg-surface p-4">
        <h3 className="mb-2 text-sm font-semibold">{t("wizard.products_heading")}</h3>
        <table className="w-full text-sm">
          <tbody>
            {form.items.map((i) => (
              <tr
                key={i.product_id}
                className="border-t border-border-subtle first:border-0"
                data-testid={`review-item-${i.product_id}`}
              >
                <td className="py-2 text-text-primary">{i.product_name_snapshot}</td>
                <td className="py-2 text-right font-mono">{i.quantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div
          className="rounded-lg border border-border-subtle bg-surface p-4"
          data-testid="review-customer-block"
        >
          <h3 className="mb-2 text-sm font-semibold">{t("wizard.customer_heading")}</h3>
          {renderAddr(customer)}
        </div>
        <div
          className="rounded-lg border border-border-subtle bg-surface p-4"
          data-testid="review-delivery-block"
        >
          <h3 className="mb-2 text-sm font-semibold">{t("wizard.delivery_heading")}</h3>
          {renderAddr(delivery)}
        </div>
      </div>
    </div>
  );
}
